import { useState, useMemo } from 'react';
import { format } from 'date-fns';
import { visitClientName, visitCaregiverName } from '../../lib/axiscare';
import DataTable from '../DataTable';

// Shared sortable visit table. mode 'standard' shows Service + Clock In;
// mode 'missed' swaps those for Reason + a follow-up Action.
const safeFormat = (dateStr, fmt) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '—';
  try { return format(d, fmt); } catch { return '—'; }
};

const STATUS_PILL = {
  Missed:    { bg: 'var(--danger-bg)',  color: 'var(--danger)' },
  Completed: { bg: 'var(--success-bg)', color: 'var(--success)' },
  Scheduled: { bg: 'var(--bg2)',        color: 'var(--text3)' },
};

// Stable identity for a visit (survives re-sorting) — used for follow-up tracking.
export const visitKey = (v) => `${v.client?.id}|${(v.scheduledStartDate || v.startDate || '').slice(0, 10)}`;

export default function VisitList({ visits, mode = 'standard', onFollowUp, followedUp = {} }) {
  const [sortKey, setSortKey] = useState('date');
  const [sortDir, setSortDir] = useState('desc');

  const sorted = useMemo(() => [...visits].sort((a, b) => {
    let av, bv;
    if (sortKey === 'date')           { av = a.scheduledStartDate || a.startDate || ''; bv = b.scheduledStartDate || b.startDate || ''; }
    else if (sortKey === 'client')    { av = visitClientName(a); bv = visitClientName(b); }
    else if (sortKey === 'caregiver') { av = visitCaregiverName(a); bv = visitCaregiverName(b); }
    else if (sortKey === 'hours')     { av = a._hours; bv = b._hours; }
    else if (sortKey === 'status')    { av = a._status; bv = b._status; }
    const cmp = typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv));
    return sortDir === 'asc' ? cmp : -cmp;
  }), [visits, sortKey, sortDir]);

  const sorting = useMemo(() => sortKey ? [{ id: sortKey, desc: sortDir === 'desc' }] : [], [sortKey, sortDir]);
  const handleSortingChange = (updaterOrValue) => {
    const next = typeof updaterOrValue === 'function' ? updaterOrValue(sorting) : updaterOrValue;
    if (!next.length) return;
    setSortKey(next[0].id);
    setSortDir(next[0].desc ? 'desc' : 'asc');
  };

  const missed = mode === 'missed';

  const columns = [
    { id: 'client', header: 'Patient', size: 160, meta: { mobile: 'primary' }, cell: info => visitClientName(info.row.original) },
    { id: 'caregiver', header: 'Caregiver', size: 150, meta: { mobile: 'secondary' }, cell: info => <span style={{ color: 'var(--text2)' }}>{visitCaregiverName(info.row.original)}</span> },
    {
      id: 'date', header: 'Date', size: 120, meta: { mobile: 'secondary' },
      cell: info => { const v = info.row.original; return safeFormat(v.scheduledStartDate || v.startDate, 'MMM d, yyyy'); },
    },
    {
      id: 'time', header: 'Time', size: 150, enableSorting: false, meta: { mobile: 'secondary' },
      cell: info => {
        const v = info.row.original;
        const startDt = v.scheduledStartDate || v.startDate;
        const endDt = v.scheduledEndDate || v.endDate;
        return <span style={{ color: 'var(--text3)', fontSize: 12 }}>{safeFormat(startDt, 'h:mm a')}{endDt && !isNaN(new Date(endDt)) ? ` – ${safeFormat(endDt, 'h:mm a')}` : ''}</span>;
      },
    },
    {
      id: 'hours', header: missed ? 'Hours Lost' : 'Hours', size: 100, meta: { mobile: 'primary' },
      cell: info => { const v = info.row.original; return <span style={{ color: missed ? 'var(--warn)' : 'var(--text2)', fontWeight: missed ? 500 : 400 }}>{v._hours > 0 ? `${v._hours.toFixed(1)}h` : '—'}</span>; },
    },
    ...(missed ? [
      {
        id: 'reason', header: 'Reason', size: 200, enableSorting: false, meta: { truncate: true, mobile: 'hidden' },
        cell: info => info.row.original.removeReason || 'Not specified',
      },
      {
        id: 'action', header: 'Action', size: 130, enableSorting: false, meta: { mobile: 'secondary' },
        cell: info => {
          const v = info.row.original;
          return followedUp[visitKey(v)] ? (
            <span style={{ fontSize: 11, background: 'var(--success-bg)', color: 'var(--success)', padding: '3px 10px', borderRadius: 20, fontWeight: 500 }}>✓ Followed up</span>
          ) : (
            <button className="btn btn-outline btn-sm" style={{ fontSize: 11, color: 'var(--warn)', borderColor: 'var(--warn-border)' }} onClick={() => onFollowUp && onFollowUp(v)}>Follow up</button>
          );
        },
      },
    ] : [
      { id: 'service', header: 'Service', size: 110, enableSorting: false, meta: { mobile: 'hidden' }, cell: info => { const v = info.row.original; return <span style={{ color: 'var(--text3)', fontSize: 12 }}>{v.service?.code || v.service?.name || '—'}</span>; } },
      { id: 'clockIn', header: 'Clock In', size: 100, enableSorting: false, meta: { mobile: 'hidden' }, cell: info => <span style={{ color: 'var(--text3)', fontSize: 12 }}>{safeFormat(info.row.original.clockIn?.time, 'h:mm a')}</span> },
      {
        id: 'status', header: 'Status', size: 110, meta: { mobile: 'primary' },
        cell: info => { const pill = STATUS_PILL[info.row.original._status] || STATUS_PILL.Scheduled; return <span style={{ fontSize: 11, fontWeight: 500, padding: '2px 8px', borderRadius: 20, background: pill.bg, color: pill.color }}>{info.row.original._status}</span>; },
      },
    ]),
  ];

  return (
    <DataTable
      tableId={`visit-list-${mode}`}
      columns={columns}
      data={sorted}
      getRowId={(v, i) => i}
      sorting={sorting}
      onSortingChange={handleSortingChange}
      manualSorting
      emptyMessage="No visits to show."
      maxHeight="calc(100vh - 380px)"
    />
  );
}
